import React, { Component } from "react";

import { observeNarrow } from "./narrowWidth";
import ThreadItems from "./threadItems";
import WidgetStatus from "./widgetStatus";

// Below this, a bubble taking 85% of the line wastes more space than it saves, so it takes all of it.

/**
 * ThreadView — a thread, read: loaded, paged, live-tailed, scrolled, and rendered.
 *
 * The thread widget's whole view minus the composer. Lifted out because a second surface shows the
 * same thing and had grown its own: Mission Control's cell screen shows a run's raw transcript for
 * debugging, which is a thread of exactly these items with exactly these needs — load a page, page
 * backwards when you scroll up, follow new items as they land, keep the view pinned to the bottom
 * unless the reader has scrolled away. Two implementations of that is two sets of scroll bugs, and
 * an improvement to either reaching only half the product.
 *
 * **Read-only by construction.** Nothing here posts. The widget layers its composer on top and hands
 * the optimistic item down through `pendingItems`, so the one surface that can write keeps the whole
 * of that machinery and the one that cannot has no way to.
 *
 * The only thing the two callers do not share is where the bytes come from, so that is a `source`:
 *
 *   key                — identity of the thread. A change reloads from scratch.
 *   listOlder({cursor}) → Promise<{ items, cursor }>  — one page, OLDEST-FIRST; cursor for the next
 *                         page back, null when exhausted
 *   listNewer({since})  → Promise<items>              — everything created after `since`,
 *                         oldest-first. Omit and the view simply does not tail.
 *   watch(cb) / unwatch(cb) — live notification that something landed. Omit for a static thread.
 *   authorName(userId) / authorProfilePath(userId)
 *
 * Props beyond `source`: `contentView`, `currentUserId`, `timezone`, `pendingItems`, `onRetry`, the
 * `emptyMessage`, and the four seams `ThreadItems` takes for a caller that knows what its items mean.
 */
export default class ThreadView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      olderCursor: null,
      hasMoreOlder: false,
      newestCreatedAt: null,
      loading: false,
      loadingOlder: false,
      loadError: null,
      unseenCount: 0
    };
    this.scrollContainerRef = React.createRef();
    // Whether the reader is at the bottom, sampled on scroll. New items follow the view only when
    // they are — otherwise arriving items would yank the page from under somebody reading history.
    this.wasAtBottom = true;
    this.mounted = false;
    this.watching = null;
  }

  componentDidMount() {
    this.mounted = true;
    this.load();
    this.observeWidth();
  }

  componentDidUpdate(prevProps) {
    // The thread ITSELF changed — a different run selected, a different alias bound. Everything
    // below is about the old one, so it is dropped rather than merged into.
    if (this.sourceKey(prevProps) !== this.sourceKey(this.props)) {
      this.load();
      return;
    }
    this.maybeAutoLoadOlder();
  }

  componentWillUnmount() {
    this.mounted = false;
    this.unwatch();
    if (this.disposeNarrow) this.disposeNarrow();
  }

  /**
   * Bubbles take a share of the width until there is too little to share, then all of it.
   *
   * The rule lives HERE because it is a property of reading a thread, not of the widget that
   * happened to implement it first: Mission Control mounted this view and its bubbles stayed at 85%
   * however narrow the pane got, because the class was toggled on the widget's own root — an element
   * Mission Control never renders.
   *
   * Observed on the scroll container, which both callers already have, rather than on a wrapper
   * introduced for the purpose — a new element in this position would sit in the flex height chain
   * that carries the scroll.
   */
  observeWidth() {
    this.disposeNarrow = observeNarrow(this.scrollContainerRef.current);
  }

  sourceKey(props) {
    return (props.source && props.source.key) || "";
  }

  /** The scroll position, as the two questions anything here asks of it. */
  atBottom() {
    const el = this.scrollContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  scrollToBottom() {
    const el = this.scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }

  handleScroll = () => {
    const el = this.scrollContainerRef.current;
    if (!el) return;
    this.wasAtBottom = this.atBottom();
    if (this.wasAtBottom && this.state.unseenCount > 0) this.setState({ unseenCount: 0 });
    // Reaching the top asks for the page before this one.
    if (el.scrollTop < 40) this.loadOlder({ auto: false });
  };

  /**
   * A first page that does not fill the viewport hides the fact that there is more.
   *
   * Without this, paging backwards is gated on a scrollbar the reader does not have: a short page in
   * a tall pane looks like the whole thread. Runs after every render, and is cheap — it returns on
   * the first of four guards in the ordinary case.
   */
  maybeAutoLoadOlder() {
    const el = this.scrollContainerRef.current;
    if (!el || !this.state.hasMoreOlder || this.state.loadingOlder || this.state.loading) return;
    if (el.scrollHeight > el.clientHeight + 4) return;
    this.loadOlder({ auto: true });
  }

  load() {
    const source = this.props.source;
    this.unwatch();
    if (!source || !source.listOlder) {
      this.setState({ items: [], olderCursor: null, hasMoreOlder: false, newestCreatedAt: null,
                      loading: false, loadError: null, unseenCount: 0 });
      return;
    }
    const key = this.sourceKey(this.props);
    this.setState({ loading: true, items: [], olderCursor: null, hasMoreOlder: false,
                    newestCreatedAt: null, loadError: null, unseenCount: 0 });
    this.wasAtBottom = true;

    source.listOlder({ cursor: null }).then((page) => {
      // The thread may have changed under an in-flight request; a late answer about a thread nobody
      // is looking at any more must not become the thread they are.
      if (!this.mounted || key !== this.sourceKey(this.props)) return;
      const items = (page && page.items) || [];
      this.setState({
        items,
        olderCursor: (page && page.cursor) || null,
        hasMoreOlder: !!(page && page.cursor),
        newestCreatedAt: items.length > 0 ? items[items.length - 1].created_at : null,
        loading: false,
        loadError: null
      }, () => this.scrollToBottom());
      this.watch();
    }, (error) => {
      if (!this.mounted || key !== this.sourceKey(this.props)) return;
      this.setState({ loading: false,
                      loadError: (error && error.message) || "Failed to load this thread." });
    });
  }

  loadOlder(opts) {
    const source = this.props.source;
    if (!source || !this.state.hasMoreOlder || this.state.loadingOlder || !this.state.olderCursor) {
      return;
    }
    const auto = !!(opts && opts.auto);
    const key = this.sourceKey(this.props);
    this.setState({ loadingOlder: true });

    // Captured BEFORE the fetch: prepending older items grows the document upward, and holding the
    // reader's place means restoring their distance from the bottom, not their scrollTop.
    const el = this.scrollContainerRef.current;
    const capturedHeight = el ? el.scrollHeight : 0;

    source.listOlder({ cursor: this.state.olderCursor }).then((page) => {
      if (!this.mounted || key !== this.sourceKey(this.props)) return;
      const known = new Set(this.state.items.map(item => item.id));
      const older = ((page && page.items) || []).filter(item => !known.has(item.id));
      const cursor = (page && page.cursor) || null;
      if (older.length === 0 && !cursor) {
        this.setState({ loadingOlder: false, hasMoreOlder: false, olderCursor: null });
        return;
      }
      this.setState({
        items: [...older, ...this.state.items],
        olderCursor: cursor,
        hasMoreOlder: cursor !== null,
        loadingOlder: false
      }, () => {
        const node = this.scrollContainerRef.current;
        if (!node) return;
        node.scrollTop = auto ? node.scrollHeight : node.scrollHeight - capturedHeight;
      });
    }, () => {
      if (this.mounted) this.setState({ loadingOlder: false });
    });
  }

  watch() {
    const source = this.props.source;
    if (!source || !source.watch || !source.listNewer) return;
    this.unwatch();
    this.watching = { source, callback: () => this.drainNewer() };
    source.watch(this.watching.callback);
  }

  unwatch() {
    if (this.watching && this.watching.source.unwatch) {
      this.watching.source.unwatch(this.watching.callback);
    }
    this.watching = null;
  }

  /**
   * Something landed: fetch everything after the newest item we hold.
   *
   * The notification says only THAT the thread moved, so the view asks what it missed rather than
   * trusting a payload — which is what makes it correct when several items land at once, and when a
   * notification is dropped entirely.
   */
  drainNewer() {
    const source = this.props.source;
    if (!source || !source.listNewer) return;
    const key = this.sourceKey(this.props);
    source.listNewer({ since: this.state.newestCreatedAt }).then((raw) => {
      if (!this.mounted || key !== this.sourceKey(this.props)) return;
      const known = new Set(this.state.items.map(item => item.id));
      const fresh = (raw || []).filter(item => item && !known.has(item.id));
      if (fresh.length === 0) return;
      this.setState({
        items: [...this.state.items, ...fresh],
        newestCreatedAt: fresh[fresh.length - 1].created_at,
        // Counted only for a reader who has scrolled away; for one at the bottom the items simply
        // arrive, which is not news.
        unseenCount: this.wasAtBottom ? 0 : this.state.unseenCount + fresh.length
      }, () => {
        if (this.wasAtBottom) this.scrollToBottom();
      });
    }, () => {
      // Ignored: the next notification retries, and a missed one is caught by the one after it.
    });
  }

  // -- what a caller with a composer needs -------------------------------------------------------
  //
  // The view is read-only and stays that way; these are how the ONE surface that can post shows its
  // own item before the server has it. Imperative because an optimistic post is an event, not a
  // state a parent can usefully hold: it happens once, at a known moment, to a known item.

  /** Show an item this view did not load — an optimistic post, before the server has it. */
  appendLocal(item) {
    this.wasAtBottom = true;
    this.setState({ items: [...this.state.items, item] }, () => this.scrollToBottom());
  }

  /**
   * Settle an optimistic item against the server's version of it.
   *
   * Three outcomes, because a post races the tail it will arrive on. Normally the optimistic row is
   * replaced in place. If a reload ran between the send and its response the row is gone — and then
   * the server item is either already here, brought in by that reload, or it is not and would be
   * lost entirely unless appended. Getting this wrong shows a message twice or drops it.
   */
  settleLocal(tempId, serverItem) {
    this.setState((prev) => {
      const index = prev.items.findIndex(item => item.__tempId === tempId);
      let items;
      if (index >= 0) {
        items = prev.items.map(item => (item.__tempId === tempId ? serverItem : item));
      } else if (prev.items.some(item => item.id === serverItem.id)) {
        items = prev.items;
      } else {
        items = [...prev.items, serverItem];
      }
      const newest = (!prev.newestCreatedAt || serverItem.created_at > prev.newestCreatedAt)
        ? serverItem.created_at : prev.newestCreatedAt;
      return { items, newestCreatedAt: newest };
    });
  }

  /** Mark an optimistic item as failed — it stays on screen, with a way to retry. */
  failLocal(tempId) {
    this.setState({
      items: this.state.items.map(item => (item.__tempId === tempId
        ? { ...item, __pending: false, __failed: true } : item))
    });
  }

  /** Drop one — a post abandoned, or retried under a new id. */
  removeLocal(tempId) {
    this.setState({ items: this.state.items.filter(item => item.__tempId !== tempId) });
  }

  /**
   * Catch up after a connection came back.
   *
   * Not the same as a reload: what was on screen is still true, and only what arrived while the
   * socket was down is missing. With nothing loaded at all there is no `since` to ask from, so this
   * falls back to loading the thread.
   */
  resync() {
    if (this.state.newestCreatedAt) this.drainNewer();
    else this.load();
  }

  render() {
    const { source, contentView, currentUserId, timezone, onRetry, emptyMessage,
            renderHead, renderContent, renderFooter, isLong, className } = this.props;
    const { items, loading, loadingOlder, loadError, unseenCount } = this.state;
    const resolveAuthorName = (source && source.authorName) || (() => "unknown");
    const resolveProfilePath = (source && source.authorProfilePath) || (() => null);

    return (
      <>
        {loadError && <WidgetStatus variant="warning">{loadError}</WidgetStatus>}
        {loading && <WidgetStatus variant="primary" loading>Loading thread…</WidgetStatus>}
        <div
          ref={this.scrollContainerRef}
          className={`playground-thread-node-messages nodrag nowheel${className ? ` ${className}` : ""}`}
          onScroll={this.handleScroll}
        >
          {/* `primary`, like every other progress alert in the product. Paging backwards is the same
              KIND of event as the first load — work in flight, not a quiet note — and the grey it
              used to wear made the two reads of one thread look like two different things. */}
          {loadingOlder && (
            <WidgetStatus variant="primary" loading>Loading earlier messages…</WidgetStatus>
          )}
          {items.length === 0 && !loading && (
            <WidgetStatus variant="light">{emptyMessage || "No messages yet."}</WidgetStatus>
          )}
          <ThreadItems
            items={[...items, ...(this.props.pendingItems || [])]}
            contentView={contentView}
            currentUserId={currentUserId}
            timezone={timezone}
            resolveAuthorName={resolveAuthorName}
            resolveProfilePath={resolveProfilePath}
            onRetry={onRetry}
            renderHead={renderHead}
            renderContent={renderContent}
            renderFooter={renderFooter}
            isLong={isLong}
          />
        </div>
        {unseenCount > 0 && (
          <div
            className="playground-thread-new-messages-pill nodrag"
            onClick={() => { this.scrollToBottom(); this.setState({ unseenCount: 0 }); }}
          >
            &darr; {unseenCount} new
          </div>
        )}
      </>
    );
  }
}
